import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Button from './Button';
import usePlayerTalents from '../hooks/usePlayerTalents';
import { 
  getTalentDisplayName,
  getTalentIcon,
  calculateTalentEffect,
  TalentType
} from '../types/talents';
import { useAppTranslation } from '../i18n/useAppTranslation';

interface TalentSummaryProps {
  onOpenTalentDialog?: () => void;
  maxDisplay?: number;
  showButton?: boolean;
  orientation?: 'horizontal' | 'vertical';
  iconSize?: number;
  className?: string;
  style?: React.CSSProperties;
}

const TalentSummary: React.FC<TalentSummaryProps> = ({
  onOpenTalentDialog,
  maxDisplay = 6,
  showButton = true,
  orientation = 'horizontal',
  iconSize = 32,
  className = '',
  style = {}
}) => {
  const { t } = useAppTranslation(["research", "common"]);
  const { playerTalents, loading } = usePlayerTalents();

  const activeTalents = playerTalents.filter(talent => talent.level > 0);
  const displayTalents = activeTalents.slice(0, maxDisplay);
  const hasMoreTalents = activeTalents.length > maxDisplay;

  if (loading) {
    return (
      <div className={`talent-summary ${className}`} style={style}>
        <div className="d-flex align-items-center gap-2">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">{t("common:labels.loading")}</span>
          </div>
          <span className="small text-muted">
            {t("research:talentsUi.loadingTalents")}
          </span>
        </div>
      </div>
    );
  }

  if (activeTalents.length === 0) {
    return (
      <div className={`talent-summary ${className}`} style={style}>
        <div className={`d-flex align-items-center gap-2 ${
          orientation === 'vertical' ? 'flex-column' : ''
        }`}>
          <i className="fas fa-star text-muted"></i>
          <span className="small text-muted">
            {t("research:talentsUi.noTalentsUnlocked")}
          </span>
          {showButton && (
            <Button
              size="sm"
              variant="outline-primary"
              onClick={onOpenTalentDialog}
              className={orientation === 'vertical' ? 'mt-2' : 'ms-2'}
            >
              {t("research:talentsUi.viewTalents")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const containerClass = `talent-summary d-flex align-items-center ${
    orientation === 'vertical' ? 'flex-column gap-2' : 'gap-3'
  } ${className}`;

  return (
    <div className={containerClass} style={style}>
      {/* Talent Icons */}
      <div className={`d-flex ${
        orientation === 'vertical' ? 'flex-column align-items-center gap-1' : 'align-items-center gap-2'
      }`}>
        {displayTalents.map((talent) => {
          const displayName = getTalentDisplayName(talent.talentId);
          const icon = getTalentIcon(talent.talentId);
          const effect = calculateTalentEffect(talent.talentId, talent.level);
          const isMaxLevel = talent.level >= talent.maxLevel;

          const tooltipContent = (
            <div style={{ 
              maxWidth: 200, 
              whiteSpace: 'normal', 
              textAlign: 'center',
              backgroundColor: '#1a1a1a',
              color: '#ffffff',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4, color: '#ffffff' }}>
                {displayName}
              </div>
              <div style={{ marginBottom: 4, color: '#cccccc' }}>
                {t("common:labels.level")} {talent.level}/{talent.maxLevel}
              </div>
              <div style={{ color: '#28a745' }}>
                {t("research:talentsUi.effect")}: {effect}
              </div>
              {isMaxLevel && (
                <div style={{ color: '#ffc107', marginTop: 4 }}>
                  <i className="fas fa-star"></i> {t("research:talentsUi.maxLevel")}
                </div>
              )}
            </div>
          );

          return (
            <OverlayTrigger
              key={talent.talentId}
              overlay={<Tooltip>{tooltipContent}</Tooltip>}
            >
              <div
                className="talent-icon-container position-relative"
                style={{
                  width: iconSize,
                  height: iconSize,
                  cursor: 'pointer'
                }}
                onClick={onOpenTalentDialog}
              >
                <img
                  src={icon}
                  alt={displayName}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '6px',
                    border: `2px solid ${
                      isMaxLevel ? '#ffc107' : '#17a2b8'
                    }`,
                    filter: isMaxLevel 
                      ? 'drop-shadow(0 0 8px rgba(255,193,7,0.5))'
                      : 'drop-shadow(0 0 4px rgba(23,162,184,0.3))'
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/assets/images/talents/default.png';
                  }}
                />
                
                {/* Level Badge */}
                <div
                  className="position-absolute"
                  style={{
                    bottom: -4,
                    right: -4,
                    background: isMaxLevel ? '#ffc107' : '#17a2b8',
                    color: isMaxLevel ? '#000' : '#fff',
                    borderRadius: '50%',
                    width: Math.max(16, iconSize * 0.4),
                    height: Math.max(16, iconSize * 0.4),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: `${Math.max(10, iconSize * 0.25)}px`,
                    fontWeight: 'bold',
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}
                >
                  {talent.level}
                </div>

                {/* Max Level Star */}
                {isMaxLevel && (
                  <div
                    className="position-absolute"
                    style={{
                      top: -4,
                      left: -4,
                      color: '#ffc107',
                      fontSize: `${Math.max(12, iconSize * 0.3)}px`,
                      textShadow: '0 0 4px rgba(0,0,0,0.8)'
                    }}
                  >
                    <i className="fas fa-star"></i>
                  </div>
                )}
              </div>
            </OverlayTrigger>
          );
        })}

        {/* More Talents Indicator */}
        {hasMoreTalents && (
          <OverlayTrigger
            overlay={
              <Tooltip>
                {t("research:talentsUi.moreTalents", {
                  count: activeTalents.length - maxDisplay,
                })}
              </Tooltip>
            }
          >
            <div
              className="talent-more-indicator d-flex align-items-center justify-content-center"
              style={{
                width: iconSize,
                height: iconSize,
                borderRadius: '6px',
                border: '2px dashed rgba(255,255,255,0.4)',
                background: 'rgba(255,255,255,0.1)',
                cursor: 'pointer',
                fontSize: `${iconSize * 0.4}px`,
                color: 'rgba(255,255,255,0.7)'
              }}
              onClick={onOpenTalentDialog}
            >
              +{activeTalents.length - maxDisplay}
            </div>
          </OverlayTrigger>
        )}
      </div>

      {/* Summary Text and Button */}
      {orientation === 'horizontal' && (
        <div className="d-flex align-items-center gap-2">
          <div className="talent-summary-text">
            <div className="small text-muted">
              {t("research:talentsUi.activeTalents", {
                count: activeTalents.length,
              })}
            </div>
          </div>
          
          {showButton && (
            <Button
              size="sm"
              variant="outline-primary"
              onClick={onOpenTalentDialog}
            >
              <i className="fas fa-cog me-1"></i>
              {t("research:talentsUi.manage")}
            </Button>
          )}
        </div>
      )}

      {orientation === 'vertical' && showButton && (
        <Button
          size="sm"
          variant="outline-primary"
          onClick={onOpenTalentDialog}
        >
          <i className="fas fa-cog me-1"></i>
          {t("research:talentsUi.manageTalents")}
        </Button>
      )}
    </div>
  );
};

export default TalentSummary;
